import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface NavbarProviderOption {
  uuid: string;
  userUuid: string;
  label: string;
  avatar?: string;
  specialty?: string;
}

interface NavbarProviderState {
  /**
   * The set every provider-filtered screen reads. Derived from the three
   * lists below — never assign to it directly.
   */
  selectedProviders: NavbarProviderOption[]; // never assign to this directly.
  /** Exactly what the user chose in the navbar dropdown, and its value. */
  pickedProviders: NavbarProviderOption[];
  /**
   * Always part of the filter, whatever the user picks. A logged-in provider
   * puts themselves here so their own data never drops out of view.
   */
  baseProviders: NavbarProviderOption[]; //only for the provider login.
  /**
   * Used only while the user has picked nothing and there is no base. A clinic
   * user puts every provider at their location here, so their screens open on
   * all of that location's data rather than on nothing.
   */
  fallbackProviders: NavbarProviderOption[];
}

const initialState: NavbarProviderState = {
  selectedProviders: [],
  pickedProviders: [],
  baseProviders: [],
  fallbackProviders: [],
};

const dedupeByUuid = (providers: NavbarProviderOption[]) => {
  const seen = new Set<string>();
  return providers.filter((provider) => {
    if (!provider?.uuid || seen.has(provider.uuid)) return false;
    seen.add(provider.uuid);
    return true;
  });
};

/**
 * Picking providers appends to the base rather than replacing it, so a
 * provider always sees their own data alongside whoever else they added. With
 * no base (clinic users) a pick replaces the fallback outright.
 */
const resolveEffective = (state: NavbarProviderState) => {
  if (state.pickedProviders.length > 0) {
    return dedupeByUuid([...state.baseProviders, ...state.pickedProviders]);
  }
  return state.baseProviders.length > 0
    ? state.baseProviders
    : state.fallbackProviders;
};

const navbarProviderSlice = createSlice({
  name: "navbarProvider",
  initialState,
  reducers: {
    setNavbarSelectedProviders: (
      state,
      action: PayloadAction<NavbarProviderOption[]>,
    ) => {
      state.pickedProviders = action.payload;
      state.selectedProviders = resolveEffective(state);
    },
    setBaseNavbarProviders: (
      state,
      action: PayloadAction<NavbarProviderOption[]>,
    ) => {
      state.baseProviders = action.payload;
      state.selectedProviders = resolveEffective(state);
    },
    setFallbackNavbarProviders: (
      state,
      action: PayloadAction<NavbarProviderOption[]>,
    ) => {
      state.fallbackProviders = action.payload;
      state.selectedProviders = resolveEffective(state);
    },
    clearNavbarSelectedProviders: (state) => {
      state.selectedProviders = [];
      state.pickedProviders = [];
      state.baseProviders = [];
      state.fallbackProviders = [];
    },
  },
});

export const {
  setNavbarSelectedProviders,
  setBaseNavbarProviders,
  setFallbackNavbarProviders,
  clearNavbarSelectedProviders,
} = navbarProviderSlice.actions;

export default navbarProviderSlice.reducer;
